import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Upload, Cpu, IdCard, Share2, Link2, FileText, ArrowRight, Zap, ShieldCheck,
} from "lucide-react";
import { HiringBrief } from "@/components/platform/HiringBrief";
import { TrustScorePanel } from "@/components/platform/TrustScorePanel";
import { JobIntakeWidget } from "@/components/employer/JobIntakeWidget";
import { SocialProof } from "@/components/marketing/SocialProof";
import { FeaturedStoryStrip } from "@/components/marketing/FeaturedStoryStrip";

const steps = [
    { icon: Upload, title: "Upload your job", desc: "LinkedIn URL, Indeed URL, PDF, Word, or manual entry — Simonara normalizes it all into a structured job profile." },
    { icon: Cpu, title: "AI analyzes the role", desc: "Simonara AI evaluates experience, transferable skills, certifications, industry background, career progression, languages, and location preferences." },
    { icon: IdCard, title: "Receive five Talent Passports", desc: "Each with an AI Match™ requirement breakdown, resume, AI summary, video interview, salary expectations, availability, skills, and certifications." },
    { icon: Share2, title: "Decide together", desc: "Share candidates with your hiring team. Comment, rate, compare, and schedule interviews — all in one workspace." },
];

const uploadFormats = ["LinkedIn URL", "Indeed URL", "PDF", "Word", "Manual entry"];

const Employers = () => {
    return (
        <div className="bg-background">
            {/* HERO */}
            <section className="pt-20 pb-16 lg:pt-28">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <p className="overline">Simonara Hire™</p>
                        <h1 className="mt-3 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground leading-[1.05]">
                            Your next hire, delivered as a <span className="text-brand-green dark:text-emerald-400">Talent Passport.</span>
                        </h1>
                        <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                            Upload a job — or paste a LinkedIn or Indeed posting — and receive five matched
                            Talent Passports with an AI Hiring Brief™ explaining every match.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link to="/employers/signup" data-testid="employers-hero-cta" className="btn-green">Find Talent <ArrowRight size={16} className="ml-2" /></Link>
                            <Link to="/pricing" data-testid="employers-pricing-link" className="btn-outline">View pricing</Link>
                            <Link to="/trust-center" data-testid="employers-trust-link" className="btn-outline inline-flex items-center gap-1.5">
                                <ShieldCheck size={14} /> Trust Center
                            </Link>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {uploadFormats.map((f) => (
                                <span key={f} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground">
                                    {f.includes("URL") ? <Link2 size={12} className="text-emerald-500" /> : <FileText size={12} className="text-emerald-500" />} {f}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <HiringBrief />
                    </div>
                </div>
            </section>

            {/* SOCIAL PROOF — edit /config/socialProof.js */}
            <SocialProof />

            {/* STEPS */}
            <section className="section-pad bg-muted/50" data-testid="employers-steps-section">
                <div className="container-page">
                    <div className="max-w-2xl">
                        <p className="overline">How it works</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            From job post to shortlist in four steps.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {steps.map((s, i) => (
                            <motion.div
                                key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                                className="rounded-2xl border border-border bg-card p-7 h-full"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="w-11 h-11 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                        <s.icon size={20} />
                                    </div>
                                    <span className="mono-score text-3xl font-bold text-border">{`0${i + 1}`}</span>
                                </div>
                                <h3 className="mt-5 font-heading font-semibold text-lg text-foreground">{s.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRUST */}
            <section className="section-pad" data-testid="employers-trust-section">
                <div className="container-page">
                    <div className="max-w-2xl">
                        <p className="overline">Simonara Verify™</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                            Every candidate arrives pre-verified.
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Premium verification services — identity, employment, references, background checks — are purchased
                            only when you request them. Your subscription stays predictable.
                        </p>
                    </div>
                    <div className="mt-12">
                        <TrustScorePanel />
                    </div>
                </div>
            </section>

            {/* REQUEST TALENT */}
            <section id="request-talent" className="section-pad bg-muted/50">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="overline">Find Talent</p>
                        <h2 className="mt-3 font-heading font-semibold text-3xl lg:text-4xl tracking-tight text-foreground">
                            Tell us who you're looking for. We'll find your best matches.
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Upload your job description — or simply paste a LinkedIn or Indeed job posting — and Simonara AI
                            will immediately begin analyzing your hiring needs to identify verified Talent Passport™ matches.
                        </p>
                        <p className="mt-3 text-muted-foreground leading-relaxed">
                            You'll receive an AI Hiring Brief explaining why each candidate is a fit, along with their
                            AI Match™ requirement breakdown, resume, AI summary, pre-recorded interview, availability,
                            salary expectations, skills, certifications, and work history.
                        </p>
                        <p className="mt-3 text-muted-foreground leading-relaxed">
                            For specialized or hard-to-fill roles, a Simonara recruiting specialist can seamlessly step in
                            to provide hands-on recruiting support whenever additional expertise is needed.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <FeaturedStoryStrip />
                        <div data-testid="employers-ai-indicator" className="mt-4 mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-accent px-4 py-2 text-sm font-medium text-accent-foreground dark:text-emerald-300">
                            <Zap size={15} className="text-emerald-500" />
                            AI begins analyzing your role immediately after submission.
                        </div>
                        <JobIntakeWidget />
                        <p data-testid="employers-trust-statement" className="mt-4 text-center text-xs text-muted-foreground">
                            Powered by Simonara AI. Enhanced by experienced recruiters when you need them.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Employers;
